import React, {useState} from 'react';
import {Box, Grid, Typography} from "@mui/material";
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Top from "../Top";
import Bottom from "../Bottom";
import GaugeChart from "../../components/GaugeChart";
import ScatterChart from "../../components/ScatterChart";
import DeviceNormalRange from "../../components/DeviceNormalRange";
import {PRESSURE_COLOR, PressureLegend} from "./HistoryDevicePressureDetail";
import * as util from "../../utils/util";
import {DeviceInfo, getPressureStatus} from "../../utils/deviceInfo";
import {pressureMonthOf, useHealthDevice} from "../../utils/healthDeviceApi";

// 월간 평균 혈압에 대한 안내 문구
function getMonthMessage(high: number, low: number, recordCount: number) {
  if (recordCount === 0) {
    return '아직 기록이 없어요';
  }
  const status = getPressureStatus(high, low).text;
  if (status === '정상') {
    return '혈압 조절을 잘 하고 있어요';
  }
  if (status === '높음') {
    return '일일 평균 혈압이 높아요';
  }
  return '일일 평균 혈압이 낮아요';
}

const HistoryDevicePressureMonthHistory = (props: { device: DeviceInfo }) => {
  const device = props.device;
  const [monthValue, setMonthValue] = useState(new Date().getMonth() + 1);

  const { data: deviceData } = useHealthDevice(0);
  const monthInfo = pressureMonthOf(deviceData, 0);
  const targetPressure = { bloodPressureHigh: monthInfo.systolicAvg, bloodPressureLow: monthInfo.diastolicAvg };
  const monthMessage = getMonthMessage(monthInfo.systolicAvg, monthInfo.diastolicAvg, monthInfo.recordCount);

  const monthSeries = [
    {
      label: '수축기',
      data: monthInfo.trend.systolic.map((value, index) => ({ x: index + 1, y: value, id: 'systolic' + index })),
    },
    {
      label: '이완기',
      data: monthInfo.trend.diastolic.map((value, index) => ({ x: index + 1, y: value, id: 'diastolic' + index })),
    },
  ];

  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title={'월간 ' + device.typeName + ' 기록'} />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%' }}>
        <Box>

          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setMonthValue((prev) => prev - 1)} sx={{ 'display': monthValue > 1 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
            {new Date().getFullYear() + '년 ' + monthValue + '월'}
            <ArrowRightIcon onClick={() => setMonthValue((prev) => prev + 1)} sx={{ 'display': monthValue < new Date().getMonth() + 1 && monthValue > 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          <Stack direction={'column'} justifyContent={'center'} alignItems={'center'} sx={{ m: '20px 0', p: 1, bgcolor: '#fff', borderRadius: '15px', boxShadow: CardShadow }}>
            <Typography sx={{ fontSize: 14, color: '#333', fontWeight: 700, mb: 1 }}>{device.name}</Typography>

            <Box sx={{ '& svg': { width: '240px !important', height: '130px !important' } }}>
              <GaugeChart targetValue={util.getPercentValueForGauge(targetPressure)} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 14, color: monthInfo.recordCount > 0 ? '#555' : '#777', fontWeight: 700 }}>{monthMessage}</Typography>
            </Box>

            <Stack direction={'row'} justifyContent={'space-evenly'} alignItems={'center'} sx={{ width: '100%', mb: 1 }}>
              <Stack direction={'column'} justifyContent={'center'} alignItems={'center'}>
                <Typography sx={{ fontSize: 14, color: '#555' }}>일일 평균 수치</Typography>
                <Typography component="div" sx={{ fontSize: 20, color: '#777', fontWeight: 700 }}>
                  {monthInfo.systolicAvg + '/' + monthInfo.diastolicAvg}
                  <Typography component="span" sx={{ fontSize: 14, color: '#777', ml: 1 }}>mmHg</Typography>
                </Typography>
              </Stack>
              <Divider orientation="vertical" variant="middle" flexItem />
              <Stack direction={'column'} justifyContent={'center'} alignItems={'center'}>
                <Typography sx={{ fontSize: 14, color: '#555' }}>정상 수치일</Typography>
                <Typography component="div" sx={{ fontSize: 20, color: '#777', fontWeight: 700 }}>
                  {util.formatComma(monthInfo.completeCount)}
                  <Typography component="span" sx={{ fontSize: 14, color: '#777', ml: 1 }}>일</Typography>
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Box sx={{ bgcolor: '#fff', p: '10px 10px 20px 10px', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack>
                <Typography component="div" sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>월간현황</Typography>
              </Stack>
            </Stack>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography component="div" sx={{ fontSize: 12, color: '#555', mr: .5 }}>이번 달의
                <Typography component="span" sx={{ fontSize: 14, color: '#0069ce', fontWeight: 700, mb: 2 }}> 혈압 </Typography>
                기록이에요.
              </Typography>
            </Box>
            <Divider />

            <Box sx={{ mt: -3.5, '& .MuiChartsAxis-tickContainer:last-child': { display: 'none' } }}>
              <ScatterChart series={monthSeries} colors={[PRESSURE_COLOR.systolic, PRESSURE_COLOR.diastolic]} />
              <PressureLegend />
            </Box>
          </Box>

          <DeviceNormalRange deviceType={device.type} />
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

const CardShadow = '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)';

export default HistoryDevicePressureMonthHistory;
